/**
 * miz-pdp.js — shared behaviour for the v5 PDP section suite.
 * Loaded deferred, vanilla JS only, idempotent (safe to include per section).
 *
 * Responsibilities:
 *  - scroll reveal (progressive enhancement, honours prefers-reduced-motion)
 *  - Section 4 delivery window (workday calculation incl. public holidays)
 *  - micro-CTA scroll back to the configurator
 *  - v5 sticky ATC (show after buybox ATC leaves viewport, hide from the
 *    review section on, BON-Loyalty launcher offset, variant/price sync)
 *  - GA4 measurement plan (brief §9): pdp_section_view, pdp_section_engage,
 *    pdp_micro_cta_click, pdp_sticky_atc_click, pdp_faq_open
 */
(function () {
  'use strict';
  if (window.__mizPdpInit) return;
  window.__mizPdpInit = true;

  var docEl = document.documentElement;

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* ── Measurement (GA4 via dataLayer) ─────────────────────────── */
  function cluster() {
    var el = document.querySelector('[data-mizpdp-cluster]');
    return el ? el.getAttribute('data-mizpdp-cluster') : '';
  }
  function scrollDepthPct() {
    var max = docEl.scrollHeight - window.innerHeight;
    if (max <= 0) return 100;
    return Math.min(100, Math.round((window.scrollY / max) * 100));
  }
  function currentSectionId() {
    var sections = document.querySelectorAll('[data-mizpdp-section]');
    var current = '';
    var mid = window.innerHeight / 2;
    sections.forEach(function (s) {
      var r = s.getBoundingClientRect();
      if (r.top <= mid && r.bottom >= 0) current = s.getAttribute('data-mizpdp-section');
    });
    return current;
  }
  function track(eventName, params) {
    var payload = Object.assign(
      { event: eventName, pdp_cluster: cluster(), device: window.innerWidth < 750 ? 'mobile' : 'desktop' },
      params || {}
    );
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
    try {
      document.dispatchEvent(new CustomEvent('mizpdp:' + eventName, { detail: payload }));
    } catch (e) {
      /* noop */
    }
  }

  ready(function () {
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ── Scroll-scrubbed reveal (all browsers) ──────────────────── *
     * True scrubbing, like the prototype's animation-timeline: view():
     * progress is derived from each element's viewport position every
     * frame, so scrolling down plays the motion and scrolling up plays
     * it backwards, proportionally. Two upgrades over the native system:
     *
     *  1. SMOOTHING — displayed progress eases toward the measured
     *     target (~100ms trail). When async buybox content (charm
     *     images, upsell cards, review widgets) shifts the layout, the
     *     native scrub snapped visibly; here the same jump glides.
     *  2. UNIVERSAL — identical behaviour in Chrome, Safari, Firefox,
     *     on any product and any placement.
     *
     * The range maths mirror the prototype's animation-ranges
     * (entry/cover/contain percentages, stagger offsets). When this
     * script runs it sets [data-reveal-ready], which switches the
     * native scrubbed reveals off in CSS (miz-pdp.css); if it never
     * runs, Chrome degrades to the native system and other browsers
     * simply show everything — nothing can get stuck invisible. */
    if (!reduceMotion) {
      var scrubRoots = document.querySelectorAll('.miz-pdp');
      if (scrubRoots.length) {
        scrubRoots.forEach(function (root) {
          root.setAttribute('data-reveal-ready', '');
        });

        var vh = window.innerHeight;
        var items = [];

        /* Where (in scrolled-distance d = vh - rect.top) a range point
         * sits. entry: element crossing in over the bottom edge;
         * cover: first pixel in → last pixel out; contain: fully
         * visible span (falls back to cover for over-tall elements). */
        function rangePoint(phase, k, h) {
          if (phase === 'entry') return k * Math.min(h, vh);
          if (phase === 'contain' && h < vh) return h + k * (vh - h);
          return k * (h + vh); /* cover */
        }

        function addItem(el, src, effect, p0, k0, p1, k1) {
          if (!el || !src) return;
          items.push({ el: el, src: src, fx: effect, p0: p0, k0: k0, p1: p1, k1: k1, cur: -1 });
        }

        var FX = {
          fade: function (el, p) {
            el.style.opacity = p;
            el.style.transform = p >= 1 ? 'none' : 'translateY(' + 18 * (1 - p) + 'px)';
          },
          lineX: function (el, p) { el.style.transform = 'scaleX(' + p + ')'; },
          lineY: function (el, p) { el.style.transform = 'scaleY(' + p + ')'; },
          diam: function (el, p) {
            el.style.transform = 'translate(-50%,-50%) rotate(45deg) scale(' + p + ')';
          },
          dash: function (el, p) { el.style.strokeDashoffset = 1 - p; },
          spine: function (el, p) {
            /* Vertical spine on mobile, horizontal from the 700px
             * container breakpoint — matches the @container rule. */
            var horizontal = el.closest('.miz-pdp').offsetWidth >= 700;
            el.style.transformOrigin = horizontal ? 'left center' : 'top center';
            el.style.transform = (horizontal ? 'scaleX(' : 'scaleY(') + p + ')';
          },
          pop: function (el, p) { el.style.transform = 'scale(' + p + ')'; },
          heroImg: function (el, p) {
            el.style.opacity = p;
            el.style.transform = 'scale(' + (1.05 - 0.05 * p) + ')';
          },
          drift: function (el, p) {
            el.style.transform = 'translateY(' + (34 - 68 * p) + 'px)';
          }
        };

        function collect() {
          items = [];
          scrubRoots.forEach(function (root) {
            root.querySelectorAll('.rv').forEach(function (el) {
              addItem(el, el, 'fade', 'entry', 0, 'entry', 0.42);
            });
            root.querySelectorAll('.stag').forEach(function (st) {
              Array.prototype.forEach.call(st.children, function (ch, i) {
                var off = i >= 1 && i <= 3 ? i * 0.07 : 0;
                addItem(ch, ch, 'fade', 'entry', off, 'entry', 0.42 + off);
              });
            });
            root.querySelectorAll('.orn').forEach(function (orn) {
              addItem(orn.querySelector('i'), orn, 'lineX', 'entry', 0, 'cover', 0.32);
              addItem(orn.querySelector('b'), orn, 'diam', 'cover', 0.08, 'cover', 0.34);
            });
            root.querySelectorAll('.s1fig .fall').forEach(function (el) {
              addItem(el, el, 'lineY', 'entry', 0.05, 'entry', 0.7);
            });
            root.querySelectorAll('.whorl').forEach(function (svg) {
              var path = svg.querySelector('path');
              if (path) {
                path.style.strokeDasharray = '1';
                addItem(path, svg, 'dash', 'entry', 0.15, 'cover', 0.38);
              }
            });
            root.querySelectorAll('.flow').forEach(function (flow) {
              addItem(flow.querySelector('.spine i'), flow, 'spine', 'contain', 0, 'contain', 0.82);
            });
            root.querySelectorAll('.step').forEach(function (step) {
              addItem(step.querySelector('.dot'), step, 'pop', 'cover', 0, 'cover', 0.2);
            });
            root.querySelectorAll('.hero img').forEach(function (img) {
              addItem(img, img, 'heroImg', 'entry', 0, 'entry', 0.55);
            });
            root.querySelectorAll('.ghost').forEach(function (el) {
              addItem(el, el, 'drift', 'cover', 0, 'cover', 1);
            });
          });
        }

        var dirty = true;
        var settled = false;
        function markDirty() {
          dirty = true;
        }

        function frame() {
          if (dirty || !settled) {
            dirty = false;
            settled = true;
            items.forEach(function (it) {
              var rect = it.src.getBoundingClientRect();
              var h = rect.height || 1;
              var d = vh - rect.top;
              var d0 = rangePoint(it.p0, it.k0, h);
              var d1 = rangePoint(it.p1, it.k1, h);
              var target = (d - d0) / (d1 - d0 || 1);
              target = target < 0 ? 0 : target > 1 ? 1 : target;
              if (it.cur < 0) {
                it.cur = target; /* first paint: no ease-in from nothing */
              } else {
                var diff = target - it.cur;
                if (diff > 0.001 || diff < -0.001) {
                  it.cur += diff * 0.22; /* ~100ms glide — masks layout shifts */
                  settled = false;
                } else {
                  it.cur = target;
                }
              }
              FX[it.fx](it.el, it.cur);
            });
          }
          requestAnimationFrame(frame);
        }

        collect();
        window.addEventListener('scroll', markDirty, { passive: true });
        window.addEventListener('resize', function () {
          vh = window.innerHeight;
          markDirty();
        });
        window.addEventListener('load', markDirty);
        /* Async content (images, app widgets) changes the page height
         * without a scroll event — re-measure when the body resizes. */
        if ('ResizeObserver' in window) {
          new ResizeObserver(markDirty).observe(document.body);
        }
        requestAnimationFrame(frame);
      }
    }

    /* ── Section view + engage tracking ─────────────────────────── */
    var sections = document.querySelectorAll('[data-mizpdp-section]');
    if ('IntersectionObserver' in window && sections.length) {
      var viewIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              track('pdp_section_view', { section_id: entry.target.getAttribute('data-mizpdp-section') });
              viewIO.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.3 }
      );
      sections.forEach(function (s) {
        viewIO.observe(s);
      });
    }
    sections.forEach(function (s) {
      var engaged = false;
      s.addEventListener('click', function (e) {
        if (engaged) return;
        if (e.target.closest('a, button, summary')) {
          engaged = true;
          track('pdp_section_engage', { section_id: s.getAttribute('data-mizpdp-section') });
        }
      });
    });

    /* ── FAQ open tracking (Section 6 + accordions in Section 2) ── */
    document.querySelectorAll('[data-mizpdp-faq] details, details[data-mizpdp-faq-item]').forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (d.open) {
          var q = d.querySelector('summary');
          track('pdp_faq_open', { faq_question: q ? q.textContent.trim().slice(0, 100) : '' });
        }
      });
    });

    /* ── Delivery window (Section 4) ────────────────────────────── */
    document.querySelectorAll('[data-mizpdp-delivery]').forEach(function (el) {
      var min = parseInt(el.getAttribute('data-days-min'), 10);
      var max = parseInt(el.getAttribute('data-days-max'), 10);
      if (isNaN(min) || isNaN(max)) return;
      var holidays = [];
      try {
        holidays = JSON.parse(el.getAttribute('data-holidays') || '[]');
      } catch (e) {
        holidays = [];
      }
      var isFreeDay = function (d) {
        var wd = d.getDay();
        if (wd === 0 || wd === 6) return true;
        var iso =
          d.getFullYear() +
          '-' +
          ('0' + (d.getMonth() + 1)).slice(-2) +
          '-' +
          ('0' + d.getDate()).slice(-2);
        return holidays.indexOf(iso) !== -1;
      };
      var addWorkdays = function (n) {
        var d = new Date();
        var added = 0;
        while (added < n) {
          d.setDate(d.getDate() + 1);
          if (!isFreeDay(d)) added++;
        }
        return d;
      };
      var locale = el.getAttribute('data-locale') || docEl.lang || 'de';
      var fmt = function (d) {
        try {
          return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: 'long' });
        } catch (e) {
          return d.toLocaleDateString();
        }
      };
      var from = el.querySelector('[data-date-from]');
      var to = el.querySelector('[data-date-to]');
      if (from) from.textContent = fmt(addWorkdays(min));
      if (to) to.textContent = fmt(addWorkdays(max));
      var fallback = el.querySelector('[data-date-fallback]');
      var live = el.querySelector('[data-date-live]');
      if (fallback && live) {
        fallback.hidden = true;
        live.hidden = false;
      }
    });

    /* ── Micro-CTA: scroll back to the configurator ─────────────── */
    function buyboxTarget() {
      return (
        document.querySelector('[data-mizpdp-buybox-anchor]') ||
        document.querySelector('.product__info-container') ||
        document.querySelector('product-info') ||
        document.querySelector('.product')
      );
    }
    document.querySelectorAll('[data-mizpdp-microcta]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        track('pdp_micro_cta_click', {
          section_id: btn.getAttribute('data-mizpdp-microcta'),
          cta_label: (btn.textContent || '').trim()
        });
        var target = buyboxTarget();
        if (target) {
          target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
        }
      });
    });

    /* ── Sticky ATC (v5 spec) ───────────────────────────────────── */
    var sticky = document.querySelector('[data-mizpdp-sticky]');
    if (sticky) initSticky(sticky, reduceMotion, buyboxTarget);
  });

  function initSticky(sticky, reduceMotion, buyboxTarget) {
    var mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
    var mainBtn = mainForm ? mainForm.querySelector('button[name="add"], [name="add"]') : null;
    if (!mainForm || !mainBtn) {
      sticky.remove();
      return;
    }

    var stickyBtn = sticky.querySelector('[data-sticky-submit]');
    var stickyLink = stickyBtn ? stickyBtn.getAttribute('data-sticky-link') || '' : '';
    /* Scoped to the button rather than the bar: an unscoped .lg/.sm
       lookup would retarget the wrong label the moment a second
       labelled element lands in the bar. */
    var stickyBtnLabelLg = stickyBtn ? stickyBtn.querySelector('.lg') : null;
    var stickyBtnLabelSm = stickyBtn ? stickyBtn.querySelector('.sm') : null;
    var priceEl = sticky.querySelector('[data-sticky-price]');
    var variantEl = sticky.querySelector('[data-sticky-variant]');

    var variantMap = {};
    try {
      variantMap = JSON.parse(sticky.querySelector('[data-sticky-variants]').textContent);
    } catch (e) {
      variantMap = {};
    }

    /* Variant / price / availability sync from the real product form */
    function syncVariant() {
      var idInput = mainForm.querySelector('input[name="id"], select[name="id"]');
      var v = idInput ? variantMap[idInput.value] : null;
      /* In link mode the button never submits, so the real ATC being
         disabled (unfilled personalisation inputs, upsell modal pending)
         must not disable it — jumping to the buybox is exactly how the
         customer resolves that. A genuinely unavailable variant still
         disables it. */
      if (v) {
        if (priceEl) priceEl.textContent = v.price;
        if (variantEl) variantEl.textContent = v.title;
        if (stickyBtn) stickyBtn.disabled = !v.available || (!stickyLink && mainBtn.disabled);
      } else if (stickyBtn) {
        stickyBtn.disabled = stickyLink ? false : mainBtn.disabled;
      }
      var soldOutLabel = sticky.getAttribute('data-label-sold-out');
      var addLabel = sticky.getAttribute('data-label-add');
      var addLabelShort = sticky.getAttribute('data-label-add-short') || addLabel;
      var label = stickyBtn && stickyBtn.disabled ? soldOutLabel : addLabel;
      var labelShort = stickyBtn && stickyBtn.disabled ? soldOutLabel : addLabelShort;
      if (stickyBtnLabelLg) stickyBtnLabelLg.textContent = label;
      if (stickyBtnLabelSm) stickyBtnLabelSm.textContent = labelShort;
    }
    syncVariant();
    mainForm.addEventListener('change', function () {
      /* variant pickers re-render the id input async — sync on next frame */
      requestAnimationFrame(syncVariant);
    });
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest && e.target.closest('variant-selects, variant-radios')) {
        setTimeout(syncVariant, 60);
      }
    });
    if ('MutationObserver' in window) {
      new MutationObserver(syncVariant).observe(mainBtn, { attributes: true, childList: true, subtree: true });
    }

    /* Visibility: show once the original ATC leaves the viewport,
       hide from the beginning of the review section (brief §1). */
    var atcGone = false;
    var reviewReached = false;
    var reviewSelector = sticky.getAttribute('data-review-selector') || '';
    var reviewAnchor = null;
    var selectors = reviewSelector.split(',').map(function (s) {
      return s.trim();
    });
    for (var i = 0; i < selectors.length && !reviewAnchor; i++) {
      if (selectors[i]) {
        try {
          reviewAnchor = document.querySelector(selectors[i]);
        } catch (e) {
          /* invalid selector from settings — skip */
        }
      }
    }

    function apply() {
      var on = atcGone && !reviewReached;
      sticky.classList.toggle('on', on);
      sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
      /* keep the hidden bar out of the tab order */
      if (on) {
        sticky.removeAttribute('inert');
      } else {
        sticky.setAttribute('inert', '');
      }
      document.body.classList.toggle('mizpdp-sticky-open', on);
      /* Direct BON launcher offset for markup the CSS selectors miss */
      var bonSel = sticky.getAttribute('data-bon-selector');
      if (bonSel) {
        try {
          document.querySelectorAll(bonSel).forEach(function (el) {
            el.style.bottom = on ? '78px' : '';
          });
        } catch (e) {
          /* invalid selector from settings — skip */
        }
      }
    }

    apply();
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          atcGone = !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0;
          apply();
        },
        { threshold: 0 }
      ).observe(mainBtn);
      if (reviewAnchor) {
        new IntersectionObserver(
          function (entries) {
            var r = entries[0];
            reviewReached = r.isIntersecting || r.boundingClientRect.top < 0;
            apply();
          },
          { rootMargin: '0px 0px -30% 0px', threshold: 0 }
        ).observe(reviewAnchor);
      }
    }

    /* Click: with a link configured the button navigates (or scrolls, for
       an #anchor) instead of adding to cart; otherwise it delegates to the
       real ATC button so upsell/input-modal validation and cart behaviour
       stay single-sourced. */
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (stickyBtn.disabled) return;
        track('pdp_sticky_atc_click', {
          scroll_depth_pct: scrollDepthPct(),
          section_at_click: currentSectionId(),
          mode: stickyLink ? 'link' : 'atc'
        });

        if (stickyLink) {
          if (stickyLink.charAt(0) === '#') {
            var anchor = null;
            try {
              anchor = document.querySelector(stickyLink);
            } catch (err) {
              anchor = null;
            }
            /* Unresolvable anchor: fall back to the buybox rather than
               leaving the button dead. */
            var target = anchor || (buyboxTarget && buyboxTarget());
            if (target) {
              target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
            } else {
              window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
            }
          } else {
            window.location.href = stickyLink;
          }
          return;
        }

        if (!reduceMotion) {
          mainBtn.scrollIntoView({ behavior: 'auto', block: 'center' });
        }
        mainBtn.click();
      });
    }

  }
})();
